/**
 * Avatar - Round profile representation with initials or image fallback, five
 * size presets, an optional status dot, and an optional colored ring.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarStatus = 'none' | 'online' | 'offline' | 'away' | 'busy';

export interface AvatarOptions extends BaseUIComponentOptions {
  /** Initials shown when no image is provided. */
  initials?: string;
  /** Image URL (when provided, replaces initials). */
  imageUrl?: string;
  /** Size preset. */
  size?: AvatarSize;
  /** Status dot. `none` hides the dot. */
  status?: AvatarStatus;
  /** Optional ring color (any CSS color or empty to disable). */
  ringColor?: string;
}

export class Avatar extends BaseUIComponent<AvatarOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Avatar',
    category: 'data',
    summary: 'Round profile picture with initials, status dot, and optional ring.',
    useCases: ['profile', 'player portrait', 'user chip', 'lobby roster'],
    optionsType: 'AvatarOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/avatar.html',
      styles: '@engine/assets/ui/styles/avatar.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private imageElement: HTMLImageElement | null = null;
  private initialsElement: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: Avatar.metadata.assetPaths.template,
      stylesPath: Avatar.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<AvatarOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      initials: '??',
      imageUrl: '',
      size: 'md',
      status: 'none',
      ringColor: '',
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      initials: this.options.initials,
      size: this.options.size,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-avatar') as HTMLElement | null;
    this.imageElement = this.layout.querySelector('[data-avatar-image]') as HTMLImageElement | null;
    this.initialsElement = this.layout.querySelector('[data-avatar-initials]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    this.applyImage();
    this.applyStatus();
    this.applyRing();
  }

  /** Update the initials fallback. */
  public setInitials(initials: string): void {
    this.options.initials = initials;
    if (this.initialsElement) this.initialsElement.textContent = initials;
  }

  /** Set or clear the avatar image URL. */
  public setImageUrl(imageUrl: string): void {
    this.options.imageUrl = imageUrl;
    this.applyImage();
  }

  /** Swap the size preset. */
  public setSize(size: AvatarSize): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(`size-${this.options.size}`);
    this.options.size = size;
    this.rootElement.classList.add(`size-${size}`);
  }

  /** Set the status dot, or `none` to hide it. */
  public setStatus(status: AvatarStatus): void {
    this.options.status = status;
    this.applyStatus();
  }

  /** Set the ring color (CSS color string), or empty to remove. */
  public setRingColor(color: string): void {
    this.options.ringColor = color;
    this.applyRing();
  }

  private applyImage(): void {
    if (!this.rootElement || !this.imageElement) return;
    const hasImage = !!this.options.imageUrl;
    this.rootElement.dataset.image = hasImage ? 'true' : 'false';
    this.imageElement.src = this.options.imageUrl;
  }

  private applyStatus(): void {
    if (!this.rootElement) return;
    if (this.options.status === 'none') {
      delete this.rootElement.dataset.status;
    } else {
      this.rootElement.dataset.status = this.options.status;
    }
  }

  private applyRing(): void {
    if (!this.rootElement) return;
    this.rootElement.style.setProperty(
      '--ui-avatar-ring',
      this.options.ringColor || 'transparent'
    );
  }

  protected override onDestroy(): void {
    this.rootElement = null;
    this.imageElement = null;
    this.initialsElement = null;
  }
}
